import React, { useState, useEffect } from 'react';
import { Activity, Search, Filter, Clock, User, Hash } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../../services/api';

const actionLabels = {
  'created': { label: 'Oluşturuldu', color: 'bg-blue-100 text-blue-700' },
  'status_changed': { label: 'Durum Değişti', color: 'bg-purple-100 text-purple-700' },
  'claimed': { label: 'Sahiplenildi', color: 'bg-emerald-100 text-emerald-700' },
  'auto_assigned': { label: 'Oto. Atandı', color: 'bg-amber-100 text-amber-700' },
  'message_added': { label: 'Mesaj', color: 'bg-green-100 text-green-700' },
  'internal_note': { label: 'Dahili Not', color: 'bg-orange-100 text-orange-700' },
  'merged': { label: 'Birleştirildi', color: 'bg-indigo-100 text-indigo-700' },
  'merged_into': { label: 'Birleştirildi', color: 'bg-indigo-100 text-indigo-700' },
  'watcher_added': { label: 'Takipçi Eklendi', color: 'bg-cyan-100 text-cyan-700' },
  'bulk_status': { label: 'Toplu Güncelleme', color: 'bg-rose-100 text-rose-700' },
  'bulk_assign': { label: 'Toplu Atama', color: 'bg-rose-100 text-rose-700' },
};

export default function ActivityLogPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterAction, setFilterAction] = useState('');

  useEffect(() => { loadLogs(); }, []);

  const loadLogs = async () => {
    try {
      const res = await api.get('/activity/all');
      setLogs(res.data);
    } catch (err) {}
    setLoading(false);
  };

  const filtered = logs.filter(log => {
    const matchSearch = !search || 
      (log.Detail || '').toLowerCase().includes(search.toLowerCase()) ||
      (log.UserName || '').toLowerCase().includes(search.toLowerCase()) ||
      String(log.TicketId).includes(search);
    const matchAction = !filterAction || log.Action === filterAction;
    return matchSearch && matchAction;
  });

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Aktivite Logları</h1>
          <p className="text-sm text-slate-500 mt-1">Tüm sistem genelinde yapılan işlemlerin kaydı</p>
        </div>
        <span className="text-sm text-slate-400">{filtered.length} kayıt</span>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Ticket no, kullanıcı veya detay ara..." />
        </div>
        <select value={filterAction} onChange={e => setFilterAction(e.target.value)}
          className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">Tüm İşlemler</option>
          {Object.entries(actionLabels).map(([key, val]) => (
            <option key={key} value={key}>{val.label}</option>
          ))}
        </select>
      </div>

      {/* Log List */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="divide-y divide-slate-100">
          {filtered.slice(0, 100).map(log => {
            const actionInfo = actionLabels[log.Action] || { label: log.Action, color: 'bg-slate-100 text-slate-600' };
            return (
              <div key={log.Id} className="flex items-start gap-4 px-5 py-4 hover:bg-slate-50 transition-colors">
                <div className="w-9 h-9 bg-gradient-to-br from-slate-100 to-slate-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Activity size={16} className="text-slate-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${actionInfo.color}`}>
                      {actionInfo.label}
                    </span>
                    <Link to={`/staff/tickets/${log.TicketId}`} className="text-xs text-blue-600 hover:underline font-mono font-medium">
                      #{log.TicketId}
                    </Link>
                    {log.UserName && (
                      <span className="text-xs text-slate-500 flex items-center gap-1">
                        <User size={10} /> {log.UserName}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-700 mt-1">{log.Detail || '-'}</p>
                  <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                    <Clock size={10} /> {new Date(log.CreatedAt).toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul' })}
                  </p>
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="text-center py-12 text-slate-400">
              <Activity size={40} className="mx-auto mb-3 opacity-30" />
              <p className="text-sm">Henüz aktivite kaydı yok</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
